import React, { useState, useEffect } from 'react';

/**
 * PWAInstallPrompt
 * Shows a branded "Install App" banner when the browser fires
 * the beforeinstallprompt event (Chrome/Edge/Android).
 * On iOS Safari it shows manual instructions since iOS doesn't
 * support the beforeinstallprompt API.
 */
export default function PWAInstallPrompt() {
    const [deferredPrompt, setDeferredPrompt]   = useState(null);
    const [showBanner, setShowBanner]           = useState(false);
    const [showIOSGuide, setShowIOSGuide]       = useState(false);
    const [installed, setInstalled]             = useState(false);

    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone;

    useEffect(() => {
        // Already installed — don't show
        if (isInStandaloneMode) return;

        // Check if user already dismissed
        const dismissed = localStorage.getItem('pwa-prompt-dismissed');
        if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

        // Android/Chrome — capture the install prompt
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShowBanner(true);
        };
        window.addEventListener('beforeinstallprompt', handler);

        // iOS — show manual guide after 3s if Safari
        if (isIOS && !isInStandaloneMode) {
            const timer = setTimeout(() => setShowIOSGuide(true), 3000);
            return () => clearTimeout(timer);
        }

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') setInstalled(true);
        setDeferredPrompt(null);
        setShowBanner(false);
    };

    const handleDismiss = () => {
        setShowBanner(false);
        setShowIOSGuide(false);
        // localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    };

    if (installed || isInStandaloneMode) return null;

    // ── Android/Chrome banner ─────────────────────────────────────────────
    if (showBanner) return (
        <div style={{
            position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9999, width: 'calc(100% - 32px)', maxWidth: 420,
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            padding: '16px 18px',
            display: 'flex', alignItems: 'center', gap: 14,
            border: '1px solid rgba(255,46,88,0.15)',
            animation: 'slideUp .3s ease',
        }}>
            <style>{`@keyframes slideUp{from{opacity:0;transform:translateX(-50%) translateY(20px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}`}</style>
            <img src="/logo192.png" alt="Percept Pulse" style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 2 }}>Install Percept Pulse</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Add to home screen for faster access</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                    onClick={handleInstall}
                    style={{ background: 'rgb(255,46,88)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                    Install
                </button>
                <button
                    onClick={handleDismiss}
                    style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, padding: '8px 10px', fontSize: 13, cursor: 'pointer' }}
                >
                    ✕
                </button>
            </div>
        </div>
    );

    // ── iOS Safari guide ──────────────────────────────────────────────────
    if (showIOSGuide && isIOS) return (
        <div style={{
            position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9999, width: 'calc(100% - 32px)', maxWidth: 380,
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            padding: '18px 20px',
            border: '1px solid rgba(255,46,88,0.15)',
            animation: 'slideUp .3s ease',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src="/logo192.png" alt="Percept Pulse" style={{ width: 36, height: 36, borderRadius: 8 }} />
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Install Percept Pulse</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>Add to your home screen</div>
                    </div>
                </div>
                <button onClick={handleDismiss} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                    { icon: '⬆️', text: 'Tap the Share button in Safari\'s toolbar' },
                    { icon: '➕', text: 'Scroll down and tap "Add to Home Screen"' },
                    { icon: '✅', text: 'Tap "Add" - the app will appear on your home screen' },
                ].map((step, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                        <span style={{ fontSize: 18, flexShrink: 0 }}>{step.icon}</span>
                        <span style={{ fontSize: 12, color: '#334155', lineHeight: 1.4 }}>{step.text}</span>
                    </div>
                ))}
            </div>
            {/* Arrow pointing to Safari's share button */}
            <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: '#94a3b8' }}>
                Look for ⬆️ at the bottom of Safari
            </div>
        </div>
    );

    return null;
}